import { cssVar, Flex, Icon, Box, Text } from '@cad-arc';
import { formatLocationSource } from '../../helpers/location-display';
import React from 'react';
import { LocationStoreView } from '../../types/locations';
import { HighlightedText } from '../../../common/components/highlighted-text';
import { ConsolidatedLocationView } from '@mark43/cad-api';
import { getMapKey } from '../../../esri/helpers/utils';
import { Tooltip } from '../../../common/components/tooltip';
import { useApplicationType } from '../../../common/hooks/use-application-type';
import {
    getLocationSummary,
    getForcedResult,
    getSingleLineFullAddressLocationSummary,
} from '../../helpers/location-summary-v2';
import { CommonSize, getBasicCommonSize } from '../../../common/helpers/utils';
import { colors } from '../../../colors/colors';

function VerifiedLocationIndicator({
    location,
    size = 'sm',
}: {
    location: ConsolidatedLocationView | LocationStoreView;
    size?: CommonSize;
}) {
    if (location.areCoordinatesVerified) {
        return (
            <Flex alignItems="center" pl={2}>
                <Icon
                    icon="VerifiedLocation"
                    color="positive.default"
                    data-testid="location-select-verified"
                    size={getBasicCommonSize(size)}
                />
                <Text
                    style={{ color: 'var(--arc-colors-positive-default)', paddingTop: '2px' }}
                    variant="headingXs"
                >
                    {formatLocationSource(location)}
                </Text>
            </Flex>
        );
    }
    return null;
}

const TAG_SIZE = "25px";

export function LocationSelectFormattedLocationDisplay({
    location,
    query,
    showVerifiedIndicator,
    showMapKeyMarker,
    index,
    isSelected,
    isHighlighted,
    showCrossStreets,
    locationTitle,
    fullAddressSingleLine,
    size = 'sm',
}: {
    query?: string;
    location: ConsolidatedLocationView | LocationStoreView;
    showVerifiedIndicator?: boolean;
    showMapKeyMarker?: boolean;
    index?: number;
    isSelected?: boolean;
    isHighlighted?: boolean;
    showCrossStreets?: boolean;
    locationTitle?: string;
    fullAddressSingleLine?: boolean;
    size?: CommonSize;
}) {
    const { isFirstResponder } = useApplicationType();
    const showLocationIcon = location.areCoordinatesVerified && !isFirstResponder;
    const locationSummary = getLocationSummary(location, showCrossStreets);
    const { firstLine, secondLine, thirdLine, fourthLine } = fullAddressSingleLine
        ? getSingleLineFullAddressLocationSummary(locationSummary, location.placeName)
        : locationSummary;

    const isBaseForcedResult =
        'isForcedResult' in location ? location.isForcedResult : !location.areCoordinatesVerified;

    const textToHighlight = query ?? '';

    return (
        <Flex alignContent="flex-start" width="max-content" maxW="100%" gap={2}>
            <Box>
                {!isBaseForcedResult && showMapKeyMarker && index !== undefined ? (
                    <Flex
                        borderRadius={3}
                        h={TAG_SIZE}
                        minWidth={TAG_SIZE}
                        bg={isSelected ? 'brand.emphasis' : 'interactive.hover'}
                        outline={
                            isSelected
                                ? `1px solid ${cssVar('arc.colors.brand.accentContent')}`
                                : undefined
                        }
                        px={2}
                        width="fit-content"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Text
                            fontSize={size}
                            fontWeight="bold"
                            casing="uppercase"
                            style={{
                                color: isSelected
                                    ? cssVar('arc.colors.brand.emphasisContent')
                                    : cssVar('arc.colors.text.primary'),
                            }}
                        >
                            {getMapKey(index)}
                        </Text>
                    </Flex>
                ) : (
                    showLocationIcon && (
                        <Icon
                            icon="Location"
                            color={isHighlighted ? colors.locationHighlightBoxText : "icon.default"}
                            display="flex"
                            size={getBasicCommonSize(size)}
                        />
                    )
                )}
            </Box>
            <Box pl={1}>
                {!isBaseForcedResult ? (
                    <>
                        <Flex alignItems="center">
                            <Tooltip overlay={locationTitle || firstLine}>
                                <Box>
                                    <HighlightedText
                                        matchPattern={textToHighlight}
                                        fontSize={size ?? 'md'}
                                        fontWeight="bold"
                                        noOfLines={1}
                                        lineHeight={TAG_SIZE}
                                    >
                                        {(locationTitle || firstLine) ?? ''}
                                    </HighlightedText>
                                </Box>
                            </Tooltip>
                            {showVerifiedIndicator && (
                                <VerifiedLocationIndicator size={size} location={location} />
                            )}
                        </Flex>
                        {locationTitle && (
                            <Flex alignItems="center">
                                <Tooltip overlay={firstLine}>
                                    <HighlightedText
                                        matchPattern={textToHighlight}
                                        color="text.secondary"
                                        fontSize={size}
                                        noOfLines={1}
                                    >
                                        {firstLine ?? ''}
                                    </HighlightedText>
                                </Tooltip>
                            </Flex>
                        )}
                        <Flex alignItems="center">
                            <Tooltip overlay={secondLine}>
                                <HighlightedText
                                    matchPattern={textToHighlight}
                                    color="text.secondary"
                                    fontSize={size}
                                    noOfLines={1}
                                >
                                    {secondLine}
                                </HighlightedText>
                            </Tooltip>
                        </Flex>
                        <Tooltip overlay={thirdLine}>
                            <Text color="text.secondary" fontSize={size} noOfLines={1}>
                                {thirdLine}
                            </Text>
                        </Tooltip>
                        {fourthLine && (
                            <Text color="text.secondary" fontSize={size} noOfLines={1}>
                                {fourthLine}
                            </Text>
                        )}
                    </>
                ) : null}

                {isBaseForcedResult && (
                    <Text color="text.secondary" fontSize={size}>
                        {getForcedResult(location)}
                    </Text>
                )}
            </Box>
        </Flex>
    );
}
